import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Star, Plus, X, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, EmptyState, GainBadge } from "../components/ui";
import { BuyDialog, AlertDialog } from "../components/Dialogs";
import InstrumentLogo from "../components/InstrumentLogo";
import { api } from "../lib/api";
import { money, isCryptoType } from "../lib/format";
import useSeo from "../lib/useSeo";
import useRefreshOnReturn from "../lib/useRefreshOnReturn";
import { useMoney } from "../context/MoneyContext";
import { useTranslation } from "react-i18next";

/**
 * Back to its own page. It had been folded into the Dashboard, which made the
 * Dashboard do two unrelated jobs — "how am I doing" and "what am I considering" —
 * and left the watchlist buried below a chart. Separate page, grouped under
 * Portfolio in the sidebar.
 */
export default function Watchlist() {
  const { t } = useTranslation();
  const { display } = useMoney();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [buy, setBuy] = useState({ open: false, ticker: "", name: "" });
  const [alert, setAlert] = useState({ open: false, ticker: "", name: "" });

  useSeo({ title: t("watchlist.title"), noindex: true });

  const load = useCallback((silent) => {
    if (!silent) setLoading(true);
    api.watchlist().then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnReturn(() => load(true));

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try { const r = await api.search(q); setResults(r.data); } catch {} finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [q]);

  const watched = new Set(items.map((i) => i.ticker));
  const add = async (r) => {
    try { await api.addWatch({ ticker: r.ticker, name: r.name, asset_type: r.asset_type }); toast.success(`${r.ticker} added`); load(); }
    catch { toast.error("Failed to add"); }
  };
  const remove = async (ticker) => {
    try { await api.removeWatch(ticker); load(); } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <span className="label">{t("watchlist.subtitle")}</span>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("watchlist.title")}</h1>
      </div>

      <Panel className="card p-5" testid="watchlist-search-panel">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input data-testid="watchlist-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder={t("watchlist.searchAdd")} className="input pl-12 pr-10 py-3 text-base" />
          {searching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-500 animate-spin" size={18} />}
        </div>
        <AnimatePresence>
          {results.length > 0 && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
              <div className="mt-3 space-y-1">
                {results.map((r) => (
                  <div key={r.ticker} data-testid={`wl-search-${r.ticker}`} className="flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-slate-50">
                    <Link to={`/instrument/${r.ticker}`} className="flex-1 min-w-0">
                      <span className="font-medium text-slate-900">{r.ticker}</span>
                      <span className="text-sm text-slate-400 ml-3 truncate">{r.name}</span>
                    </Link>
                    {watched.has(r.ticker) ? (
                      <span className="pill bg-emerald-50 text-emerald-600 flex items-center gap-1"><Star size={12} className="fill-emerald-500 text-emerald-500" /> {t("instrument.watching")}</span>
                    ) : (
                      <button data-testid={`wl-add-${r.ticker}`} onClick={() => add(r)} className="btn-primary py-1.5 px-3 text-sm"><Plus size={14} /> {t("instrument.watch")}</button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Panel>

      {loading ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState testid="watchlist-empty" title={t("watchlist.emptyTitle")} description={t("watchlist.emptyDesc")} />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((it, idx) => (
            <Panel key={it.ticker} delay={idx * 0.04} className="card p-5 hover:shadow-soft transition-shadow group" testid={`watch-card-${it.ticker}`}>
              <div className="flex items-start justify-between">
                <Link to={`/instrument/${it.ticker}`} className="flex items-center gap-2 min-w-0">
                  <InstrumentLogo logo={it.logo} ticker={it.ticker} size={40} rounded="rounded-xl" />
                  <div className="min-w-0">
                    <div className="font-heading font-semibold tracking-tight">{it.ticker}</div>
                    <div className="text-xs text-slate-400 truncate max-w-[150px]">{it.name}</div>
                  </div>
                </Link>
                <button data-testid={`wl-remove-${it.ticker}`} onClick={() => remove(it.ticker)}
                        className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"><X size={18} /></button>
              </div>
              <div className="mt-5 flex items-end justify-between">
                <div className="tabular text-2xl font-heading font-semibold">
                  {it.price_usd != null ? display(it.price_usd).primary
                    : it.price ? money(it.price, it.currency, { crypto: isCryptoType(it.asset_type) }) : "—"}
                </div>
                <GainBadge value={it.change_pct || 0} />
              </div>
              <div className="mt-4 flex gap-2">
                <button data-testid={`wl-buy-${it.ticker}`} className="btn-outline flex-1 py-2 text-sm" onClick={() => setBuy({ open: true, ticker: it.ticker, name: it.name })}><Plus size={14} /> {t("common.buy")}</button>
                <button data-testid={`wl-alert-${it.ticker}`} className="btn-outline flex-1 py-2 text-sm" onClick={() => setAlert({ open: true, ticker: it.ticker, name: it.name })}><Bell size={14} /> {t("alerts.newAlert")}</button>
              </div>
            </Panel>
          ))}
        </div>
      )}

      <BuyDialog open={buy.open} ticker={buy.ticker} name={buy.name} onClose={() => setBuy({ open: false, ticker: "", name: "" })} onDone={load} />
      <AlertDialog open={alert.open} ticker={alert.ticker} name={alert.name} onClose={() => setAlert({ open: false, ticker: "", name: "" })} />
    </div>
  );
}
